/**
 * Chat-Sound-Notification (Joerg 2026-07-24).
 *
 * Synthetischer 2-Ton-Chirp per WebAudio-API — kein Asset, kein Netzwerk,
 * funktioniert offline. Klingt technisch aber unauffaellig (~180ms gesamt).
 *
 * Autoplay-Policy: der AudioContext muss von einer User-Gesture aus
 * angestossen werden. Wir lazy-instanziieren beim ersten Aufruf und
 * versuchen `resume()` — schlaegt der erste Versuch fehl (vor User-Interact),
 * bleibt der Ton stumm bis der User etwas anklickt und der Kontext-Zustand
 * spaeter auf `running` steht.
 *
 * User-Toggle: `localStorage['onvice.chat.sound_enabled']` (Default true).
 * Reines Frontend-Setting — kein Backend-Roundtrip.
 */

const LS_KEY = 'onvice.chat.sound_enabled';

let ctx: AudioContext | null = null;
let unlockAttached = false;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch {
    return null;
  }
  return ctx;
}

/**
 * Joerg 2026-07-24 (zweite Runde): Autoplay-Policy freischalten sobald der
 * User irgendwo im Portal geklickt/getippt/gescrollt hat. Ohne das bleibt
 * der AudioContext dauerhaft `suspended`, weil `ctx.resume()` ohne aktive
 * User-Gesture rejected wird — und das erste Chat-Beep verschwindet stumm.
 *
 * Der Listener haengt sich nur einmal an, kickt beim ersten Event
 * `ctx.resume()` und entfernt sich selbst. Muss vom Portal-Bootstrap
 * einmal aufgerufen werden (main.tsx / PortalShell).
 */
export function ensureAudioUnlockListener(): void {
  if (unlockAttached) return;
  if (typeof window === 'undefined') return;
  unlockAttached = true;
  const unlock = () => {
    const c = getCtx();
    if (c && c.state === 'suspended') {
      void c.resume().catch(() => { /* fail-soft */ });
    }
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
    window.removeEventListener('touchstart', unlock, true);
  };
  window.addEventListener('pointerdown', unlock, { capture: true, once: false });
  window.addEventListener('keydown', unlock, { capture: true, once: false });
  window.addEventListener('touchstart', unlock, { capture: true, once: false });
}

export function isChatSoundEnabled(): boolean {
  try {
    const v = localStorage.getItem(LS_KEY);
    // Default: an. Nur wenn explizit auf "false" gesetzt, ist stumm.
    return v !== 'false';
  } catch {
    return true;
  }
}

export function setChatSoundEnabled(on: boolean): void {
  try {
    localStorage.setItem(LS_KEY, on ? 'true' : 'false');
  } catch {
    // fail-soft
  }
}

/** Kurzer 2-Ton-Chirp: 800 Hz -> 1200 Hz, ~180 ms, sanftes Fade-out. */
export function playChatBeep(): void {
  if (!isChatSoundEnabled()) return;
  const c = getCtx();
  if (!c) return;
  // Versuch: AudioContext lief evtl. gesuspendet an (vor erster User-Gesture)
  if (c.state === 'suspended') {
    void c.resume().catch(() => { /* still nothing */ });
  }
  if (c.state !== 'running') return;

  try {
    const now = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, now);
    osc.frequency.linearRampToValueAtTime(1200, now + 0.09);
    // Envelope: schneller Attack, langsamer Decay
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    osc.connect(gain).connect(c.destination);
    osc.start(now);
    osc.stop(now + 0.2);
  } catch {
    // fail-soft
  }
}
